import {
	AfterViewInit,
	Component,
	EventEmitter,
	forwardRef,
	Input,
	Output,
} from '@angular/core';
import {
	ControlValueAccessor,
	FormArray,
	FormBuilder,
	FormGroup,
	NG_VALUE_ACCESSOR,
} from '@angular/forms';

type otpLength = 4 | 6;

@Component({
	selector: 'cmv-otp-input',
	templateUrl: './otp-input.component.html',
	styleUrls: ['./otp-input.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => OtpInputComponent),
			multi: true,
		},
	],
})
export class OtpInputComponent implements ControlValueAccessor, AfterViewInit {
	@Input() otpLength: otpLength = 6;
	@Output() otpPasted: EventEmitter<void> = new EventEmitter();

	otpForm: FormGroup;

	onChange: (value: string) => void = () => {};
	onTouched: () => void = () => {};

	constructor(private formBuilder: FormBuilder) {
		this.otpForm = this.formBuilder.group({
			otp: this.formBuilder.array([]),
		});
	}

	ngAfterViewInit(): void {
		for (let i = 0; i < this.otpLength; i++) {
			const newControl = this.formBuilder.control('');
			this.otpArray.push(newControl);
		}
		this.focusOtpInputAt(0);
	}

	focusOtpInputAt(index: number = 0) {
		setTimeout(() => {
			const initialInput = document.querySelector(
				`input[data-index="${index}"]`
			) as HTMLInputElement;
			initialInput?.focus();
		});
	}

	get otpArray(): FormArray {
		return this.otpForm?.get('otp') as FormArray;
	}

	get value(): string {
		return this.otpArray.value.join('');
	}

	writeValue(value: string): void {
		if (value.length === 0 && this.otpArray.value.length > 0) {
			this.otpArray.setValue(['', '', '', '', '', '']);
			this.otpArray.markAsUntouched();
			return;
		}
		this.otpArray.setValue(value.split(''));
	}
	registerOnChange(fn: (value: string) => void): void {
		this.onChange = fn;
	}
	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}
	setDisabledState(disabled: boolean): void {
		this.otpArray.controls.forEach(control => {
			disabled
				? control.disable({ onlySelf: true })
				: control.enable({ onlySelf: true });
		});
	}

	handleInput(index: number) {
		const value = this.otpArray.at(index).value;
		const onlyOneDigitsRegex = /^\d{1}$/;
		if (value === '' || !onlyOneDigitsRegex.test(value)) {
			this.otpArray.at(index).setValue('');
			return;
		}

		this.otpArray.at(index).setValue(value);
		this.onChange(this.value);
		this.onTouched();

		this.focusNextInput(index);
	}
	focusNextInput(index: number) {
		if (index < this.otpLength - 1) {
			const nextInput = document.querySelector(
				`input[data-index="${index + 1}"]`
			) as HTMLInputElement;
			nextInput?.focus();
		}
	}

	handleKeydown(event: KeyboardEvent, index: number) {
		if (event.key === 'Backspace') {
			event.preventDefault();
			if (this.otpArray.at(index).value !== '') {
				this.otpArray.at(index).setValue('');
				this.onChange(this.value);
				this.onTouched();
				return;
			}

			this.focusPreviousInput(index);
		}
	}

	focusPreviousInput(index: number) {
		if (index > 0) {
			const previousInput = document.querySelector(
				`input[data-index="${index - 1}"]`
			) as HTMLInputElement;
			previousInput?.focus();
		}
	}

	handlePaste(event: ClipboardEvent) {
		event.preventDefault();
		const value = event.clipboardData?.getData('Text');
		const onlyFourDigitsRegex = new RegExp(`^\\d{${this.otpLength}}$`);
		if (
			!value ||
			value.length < this.otpLength ||
			!onlyFourDigitsRegex.test(value)
		) {
			return;
		}
		this.otpArray.setValue(value.split(''));
		this.onChange(this.value);
		this.onTouched();
		const lastInput = document.querySelector(
			`input[data-index="${this.otpLength - 1}"]`
		) as HTMLInputElement;
		lastInput?.focus();
		lastInput?.blur();
		this.otpPasted.emit();
	}
}
